"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EVALUATOR_BLOCK_METADATA = exports.JobConfigExecutionMode = exports.PausedEvaluatorDisplayState = void 0;
exports.isJobConfigBlocked = isJobConfigBlocked;
exports.isJobConfigExecutable = isJobConfigExecutable;
exports.isJobConfigExecutableForExecutionMode = isJobConfigExecutableForExecutionMode;
exports.getEvaluatorBlockMetadata = getEvaluatorBlockMetadata;
exports.getEvaluatorBlockResolutionPath = getEvaluatorBlockResolutionPath;
exports.getBlockReasonForInvalidModelConfig = getBlockReasonForInvalidModelConfig;
exports.deriveEvaluatorDisplayState = deriveEvaluatorDisplayState;
exports.deriveEvaluatorDisplayStateFromExecutionCounts = deriveEvaluatorDisplayStateFromExecutionCounts;
const client_1 = require("@prisma/client");
const zod_1 = require("zod");
exports.PausedEvaluatorDisplayState = "PAUSED";
exports.JobConfigExecutionMode = zod_1.z.enum(["LIVE", "MANUAL"]);
function isJobConfigBlocked(config) {
    return config.blockedAt != null;
}
function isJobConfigExecutable(config) {
    return config.status === client_1.JobConfigState.ACTIVE && !isJobConfigBlocked(config);
}
function isJobConfigExecutableForExecutionMode(config, executionMode) {
    if (executionMode === "MANUAL") {
        // Manual batch runs bypass only the live-traffic toggle, not blocked configs.
        return !isJobConfigBlocked(config);
    }
    return isJobConfigExecutable(config);
}
exports.EVALUATOR_BLOCK_METADATA = {
    LLM_CONNECTION_AUTH_INVALID: {
        message: "Evaluator paused: LLM authentication failed. Update the LLM connection used by this evaluator and then reactivate it.",
        shortLabel: "Authentication failed",
    },
    LLM_CONNECTION_BILLING_EXHAUSTED: {
        message: "Evaluator paused: the LLM provider reported exhausted credits or an exceeded spend budget. Add credits or raise the limit in your provider account, then reactivate the evaluator.",
        shortLabel: "Provider credits exhausted",
    },
    LLM_CONNECTION_ENDPOINT_UNREACHABLE: {
        message: "Evaluator paused: the LLM connection's endpoint hostname could not be resolved. Fix the base URL of the LLM connection used by this evaluator, then reactivate it.",
        shortLabel: "Endpoint unreachable",
    },
    LLM_CONNECTION_MISSING: {
        message: "Evaluator paused: no LLM connection found for the provider used by this evaluator. Add or restore the LLM connection, then reactivate it.",
        shortLabel: "LLM connection missing",
    },
    DEFAULT_EVAL_MODEL_MISSING: {
        message: "Evaluator paused: no default evaluation model is configured. Set a default evaluation model or update the evaluator template, then reactivate it.",
        shortLabel: "Default evaluation model missing",
    },
    EVAL_MODEL_CONFIG_INVALID: {
        message: "Evaluator paused: no valid evaluation model is configured. Update the evaluator template or default evaluation model, then reactivate it.",
        shortLabel: "Evaluation model invalid",
    },
    EVAL_MODEL_UNAVAILABLE: {
        message: "Evaluator paused: model not found or unavailable. Update the evaluator template or default evaluation model, then reactivate it.",
        shortLabel: "Model unavailable",
    },
    PROVIDER_ACCOUNT_NOT_READY: {
        message: "Evaluator paused: provider account setup is incomplete. Complete the provider setup and then reactivate the evaluator.",
        shortLabel: "Provider account setup incomplete",
    },
};
function getEvaluatorBlockMetadata(reason) {
    return exports.EVALUATOR_BLOCK_METADATA[reason];
}
function getEvaluatorBlockResolutionPath(params) {
    const { projectId, blockReason, templateId } = params;
    if (blockReason === client_1.EvaluatorBlockReason.LLM_CONNECTION_AUTH_INVALID ||
        blockReason === client_1.EvaluatorBlockReason.LLM_CONNECTION_BILLING_EXHAUSTED ||
        blockReason === client_1.EvaluatorBlockReason.LLM_CONNECTION_ENDPOINT_UNREACHABLE ||
        blockReason === client_1.EvaluatorBlockReason.LLM_CONNECTION_MISSING) {
        return `/project/${projectId}/settings/llm-connections`;
    }
    if (templateId) {
        return `/project/${projectId}/evals/templates/${templateId}`;
    }
    return `/project/${projectId}/evals`;
}
function getBlockReasonForInvalidModelConfig(params) {
    const { templateProvider, templateModel, error } = params;
    if (error.includes("API key for provider")) {
        return client_1.EvaluatorBlockReason.LLM_CONNECTION_MISSING;
    }
    if (!templateProvider || !templateModel) {
        return client_1.EvaluatorBlockReason.DEFAULT_EVAL_MODEL_MISSING;
    }
    return client_1.EvaluatorBlockReason.EVAL_MODEL_CONFIG_INVALID;
}
function deriveEvaluatorDisplayState(params) {
    const { status, blockedAt, timeScope, hasPendingJobs, totalJobCount } = params;
    if (status === client_1.JobConfigState.INACTIVE) {
        return client_1.JobConfigState.INACTIVE;
    }
    if (blockedAt) {
        return exports.PausedEvaluatorDisplayState;
    }
    if (timeScope.length === 1 &&
        timeScope[0] === "EXISTING" &&
        !hasPendingJobs &&
        totalJobCount > 0) {
        return "FINISHED";
    }
    return client_1.JobConfigState.ACTIVE;
}
function deriveEvaluatorDisplayStateFromExecutionCounts(params) {
    const { status, blockedAt, timeScope, executionCounts = [] } = params;
    return deriveEvaluatorDisplayState({
        status,
        blockedAt,
        timeScope,
        hasPendingJobs: executionCounts.some((executionCount) => executionCount.status === client_1.JobExecutionStatus.PENDING),
        totalJobCount: executionCounts.reduce((total, executionCount) => total + executionCount.count, 0),
    });
}
